'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'
import { useFocusTrap } from '@sifly/shared-ui/overlay'

/** Tailwind's `lg` — the width at which the 'full' layout drops the step sheet
 *  (`lg:hidden`) and hands over to the inline panel. */
const DESKTOP_QUERY = '(min-width: 1024px)'

export type UseStepSheetModalArgs = {
  /** The step sheet is rendered — i.e. `phase !== 'choose'`. */
  open: boolean
  /** `chrome.overlayAllBreakpoints`: 'embed' shows the sheet at every width,
   *  'full' only below `lg`. */
  allBreakpoints: boolean
  /** Escape handler. Pass the form's `onExpire` so Escape releases the booking
   *  hold exactly like the sheet's own Back button — one close path, not two. */
  onClose: () => void
  /** Where focus lands on close — the form root. See the note on the restore
   *  effect for why the opening control can't serve as the return target. */
  returnFocusRef: RefObject<HTMLElement | null>
}

/**
 * Modal semantics for the booking step sheet: focus trap + invoker restore,
 * Escape → `onClose`, and a body scroll lock — but only while the sheet is the
 * surface the visitor can actually see. In the 'full' layout the sheet stays
 * mounted at desktop widths behind a `lg:hidden`, with the inline panel visible
 * instead; trapping focus into a `display:none` element there would strand
 * keyboard users on a page they can't reach. Hence the media query rather than
 * `open` alone. Where the sheet IS visible — 'full' below `lg`, 'embed' at every
 * width — behavior is identical.
 *
 * Reuses `useFocusTrap` from @sifly/shared-ui, the same hook `ModalShell` uses.
 * The sheet is a full-bleed surface rather than a card, so it can't just BE a
 * `ModalShell`; this hook gives it the same guarantees.
 */
export function useStepSheetModal({
  open,
  allBreakpoints,
  onClose,
  returnFocusRef,
}: UseStepSheetModalArgs) {
  const overlayRef = useRef<HTMLDivElement>(null)

  // Starts `false` (the server has no viewport) and syncs on mount. The sheet
  // only opens after a click — long after this has settled — so the initial
  // value is never the one a trap decision gets made on.
  const [desktop, setDesktop] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(DESKTOP_QUERY)
    setDesktop(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setDesktop(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const active = open && (allBreakpoints || !desktop)

  useFocusTrap({ active, containerRef: overlayRef, onEscape: onClose })

  // `useFocusTrap` restores focus to whatever was focused when the trap engaged.
  // That's a no-op here: the Reserve control that opens the sheet unmounts as the
  // phase leaves 'choose' (`showStickyBar` / `showDesktopInlinePanel`), so the
  // browser has already dropped focus to <body> before the trap records it. Land
  // focus on the form instead, so the next Tab resumes at the booking panel
  // rather than the top of the page. Declared AFTER the trap so this cleanup
  // runs after its restore and wins.
  useEffect(() => {
    if (!active) return
    return () => {
      const anchor = returnFocusRef.current
      if (!anchor) return
      // Only step in when the trap's own restore found nothing to return to.
      if (document.activeElement && document.activeElement !== document.body) return
      anchor.focus()
    }
  }, [active, returnFocusRef])

  // Lock background scroll while the sheet owns the viewport; restore whatever
  // the page had before on close, unmount, or a resize back to the inline panel.
  useEffect(() => {
    if (!active) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [active])

  return overlayRef
}
